import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const FilterChips = ({ onFilterChange }) => {
  const [activeFilters, setActiveFilters] = useState({
    genre: null,
    rating: null,
    releaseDate: null,
    tier: null,
    sortBy: 'trending'
  });

  const [showAllGenres, setShowAllGenres] = useState(false);

  const genres = [
    'Action', 'Adventure', 'RPG', 'Strategy', 'Puzzle', 'Racing', 
    'Sports', 'Simulation', 'Horror', 'Platformer', 'Fighting', 'Shooter'
  ];

  const visibleGenres = showAllGenres ? genres : genres.slice(0, 6);

  const ratingOptions = [
    { label: '4.5+ Stars', value: '4.5' },
    { label: '4.0+ Stars', value: '4.0' },
    { label: '3.5+ Stars', value: '3.5' }
  ];

  const releaseDateOptions = [
    { label: 'This Week', value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'This Year', value: 'year' }
  ];

  const tierOptions = [
    { label: 'Free to Play', value: 'free', icon: 'Gift' },
    { label: 'Premium', value: 'premium', icon: 'Crown' }
  ];

  const sortOptions = [
    { label: 'Trending', value: 'trending' },
    { label: 'Newest', value: 'newest' },
    { label: 'Highest Rated', value: 'rating' },
    { label: 'Most Played', value: 'popular' }
  ];

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...activeFilters,
      [filterType]: activeFilters[filterType] === value ? null : value
    };
    setActiveFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      genre: null,
      rating: null,
      releaseDate: null,
      tier: null,
      sortBy: 'trending'
    };
    setActiveFilters(clearedFilters);
    onFilterChange?.(clearedFilters);
  };

  const hasActiveFilters = activeFilters.genre || activeFilters.rating || activeFilters.releaseDate || activeFilters.tier;

  return (
    <div className="mb-6 space-y-4">
      {/* Sort Options */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Sort by:</span>
        {sortOptions.map((option) => (
          <Button
            key={option.value}
            variant={activeFilters.sortBy === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('sortBy', option.value)}
            className="whitespace-nowrap"
          >
            {option.label}
          </Button>
        ))}
      </div>

      {/* Filter Chips */}
      <div className="space-y-3">
        {/* Genre Filters */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Genres:</span>
            {genres.length > 6 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllGenres(!showAllGenres)}
                iconName={showAllGenres ? 'ChevronUp' : 'ChevronDown'}
                iconPosition="right"
              >
                {showAllGenres ? 'Show Less' : 'Show More'}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {visibleGenres.map((genre) => (
              <Button
                key={genre}
                variant={activeFilters.genre === genre ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('genre', genre)}
                className="text-xs"
              >
                {genre}
              </Button>
            ))}
          </div>
        </div>

        {/* Tier Filter - Prominent placement */}
        <div>
          <span className="text-sm font-medium text-muted-foreground mb-2 block">Game Access:</span>
          <div className="flex flex-wrap gap-2">
            {tierOptions.map((option) => (
              <Button
                key={option.value}
                variant={activeFilters.tier === option.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('tier', option.value)}
                iconName={option.icon}
                iconPosition="left"
                className="text-xs"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Rating and Release Date Filters */}
        <div className="flex flex-wrap gap-4">
          {/* Rating Filter */}
          <div>
            <span className="text-sm font-medium text-muted-foreground mb-2 block">Rating:</span>
            <div className="flex flex-wrap gap-2">
              {ratingOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={activeFilters.rating === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange('rating', option.value)}
                  iconName="Star"
                  iconPosition="left"
                  className="text-xs"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Release Date Filter */}
          <div>
            <span className="text-sm font-medium text-muted-foreground mb-2 block">Released:</span>
            <div className="flex flex-wrap gap-2">
              {releaseDateOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={activeFilters.releaseDate === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange('releaseDate', option.value)}
                  iconName="Calendar"
                  iconPosition="left"
                  className="text-xs"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground">
              {[activeFilters.genre, activeFilters.rating, activeFilters.releaseDate, activeFilters.tier].filter(Boolean).length} filter(s) active
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear All
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterChips;
